import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, Plus, Trash2, Loader2, Tag, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, shortId } from '@/lib/utils';


const PLATFORMS = [
  { value: 'reddit', label: 'Reddit' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'linkedin', label: 'LinkedIn' },
];


export default function Credentials() {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [platformFilter, setPlatformFilter] = React.useState('all');
  const [tagFilter, setTagFilter] = React.useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['credentials', platformFilter],
    queryFn: () => {
      const params = platformFilter !== 'all' ? { platform: platformFilter } : {};
      return api.get('/credentials', { params }).then(r => r.data);
    },
  });
  const { data: tagData } = useQuery({ queryKey: ['credentials-tags'], queryFn: () => api.get('/credentials/tags').then(r => r.data) });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/credentials/${id}`),
    onSuccess: () => { toast.success('Credential removed'); qc.invalidateQueries({ queryKey: ['credentials'] }); qc.invalidateQueries({ queryKey: ['credentials-tags'] }); },
  });

  const items = React.useMemo(() => {
    const arr = data?.items || [];
    if (tagFilter === 'all') return arr;
    return arr.filter(c => (c.tags || []).includes(tagFilter));
  }, [data, tagFilter]);

  return (
    <div className="space-y-6" data-testid="credentials-page">
      <PageHeader
        eyebrow="Infrastructure"
        title="Credentials"
        description="Platform accounts (Reddit / X / LinkedIn). Tags group credentials across campaigns. Passwords Fernet-encrypted."
        actions={<Button onClick={() => setOpen(true)} data-testid="credentials-new-button"><Plus className="mr-2 h-4 w-4" /> Add credential</Button>}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">Platform</Label>
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="w-[160px]" data-testid="credentials-platform-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(tagData?.tags || []).length > 0 && (
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">Tag</Label>
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-[200px]" data-testid="credentials-tag-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {tagData.tags.map(t => (
                  <SelectItem key={t.name} value={t.name}>{t.name} ({t.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--text-3))]" data-testid="loading-skeleton">Loading…</div>
        ) : !items.length ? (
          <EmptyState icon={ShieldCheck} title="No credentials" description="Add a credential to assign to an agent." action={<Button size="sm" onClick={() => setOpen(true)}>Add credential</Button>} />
        ) : (
          <Table data-testid="credentials-table">
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`credentials-row-${c.id}`}>
                  <TableCell>
                    <div className="font-medium">{c.label}</div>
                    <div className="font-mono text-xs text-[hsl(var(--text-3))]">{shortId(c.id)}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
                      c.platform === 'reddit' ? 'border-orange-500/30 bg-orange-500/10 text-orange-400'
                      : c.platform === 'linkedin' ? 'border-sky-500/30 bg-sky-500/10 text-sky-400'
                      : c.platform === 'x' ? 'border-white/20 bg-white/5 text-[hsl(var(--text-1))]'
                      : 'border-white/10 bg-[hsl(var(--surface-2))] text-[hsl(var(--text-3))]'}`}>
                      {c.platform}
                    </span>
                  </TableCell>
                  <TableCell><span className="font-mono text-xs text-[hsl(var(--text-2))]">{c.username}</span></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).map(t => (
                        <span key={t} className="inline-flex items-center gap-0.5 rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 text-[10px] font-mono text-[hsl(var(--text-2))]">
                          <Tag className="h-2.5 w-2.5" /> {t}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs text-[hsl(var(--text-3))]">{formatDate(c.created_at)}</span></TableCell>
                  <TableCell>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="rounded p-1.5 text-[hsl(var(--text-3))] hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--danger))] transition-colors" aria-label="Delete" data-testid={`credentials-delete-${c.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove credential?</AlertDialogTitle>
                          <AlertDialogDescription>Agents depending on it will stop until a replacement is attached.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => delMut.mutate(c.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AddCredentialDrawer open={open} onClose={() => setOpen(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['credentials'] }); qc.invalidateQueries({ queryKey: ['credentials-tags'] }); setOpen(false); }}
        existingTags={tagData?.tags || []} />
    </div>
  );
}


function AddCredentialDrawer({ open, onClose, onCreated, existingTags }) {
  const [form, setForm] = React.useState({ label: '', platform: 'reddit', username: '', password: '', tags: [] });
  const [tagInput, setTagInput] = React.useState('');

  const mut = useMutation({
    mutationFn: (p) => api.post('/credentials', p).then(r => r.data),
    onSuccess: () => { toast.success('Credential added'); onCreated(); setForm({ label: '', platform: 'reddit', username: '', password: '', tags: [] }); setTagInput(''); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const addTag = (raw) => {
    const t = (raw || tagInput).trim().toLowerCase().slice(0, 40);
    if (!t || form.tags.includes(t)) { setTagInput(''); return; }
    setForm({ ...form, tags: [...form.tags, t] });
    setTagInput('');
  };
  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter(x => x !== t) });

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-[520px] flex flex-col" data-testid="right-drawer">
        <SheetHeader>
          <SheetTitle>Add credential</SheetTitle>
          <SheetDescription>Passwords are Fernet-encrypted and never returned from the API.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          <div className="space-y-1.5"><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="u/myaccount" data-testid="cred-label-input" /></div>
          <div className="space-y-1.5">
            <Label>Platform</Label>
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
              <SelectTrigger data-testid="cred-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="cred-username-input" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cred-password-input" /></div>

          <div className="space-y-1.5">
            <Label>Tags</Label>
            <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] px-2.5 py-2 space-y-2">
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-1" data-testid="tag-chips">
                  {form.tags.map(t => (
                    <span key={t} className="inline-flex items-center gap-1 rounded border border-white/10 bg-[hsl(var(--surface-3))] px-1.5 py-0.5 text-[11px] font-mono">
                      {t}
                      <button type="button" onClick={() => removeTag(t)} className="text-[hsl(var(--text-3))] hover:text-[hsl(var(--danger))]" aria-label={`Remove tag ${t}`} data-testid={`remove-tag-${t}`}>
                        <XIcon className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                    e.preventDefault();
                    addTag();
                  } else if (e.key === 'Backspace' && !tagInput && form.tags.length) {
                    removeTag(form.tags[form.tags.length - 1]);
                  }
                }}
                placeholder="Type a tag, press Enter (e.g. seo-campaign)"
                className="h-7 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-xs"
                data-testid="tag-input"
              />
              {existingTags && existingTags.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1 border-t border-white/5">
                  <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))] w-full">Existing tags:</span>
                  {existingTags.slice(0, 12).map(t => (
                    <button key={t.name} type="button" onClick={() => addTag(t.name)}
                      disabled={form.tags.includes(t.name)}
                      className="rounded border border-white/5 bg-[hsl(var(--surface-3))] px-1.5 py-0.5 text-[10px] font-mono text-[hsl(var(--text-2))] hover:border-[hsl(var(--primary))]/30 hover:text-[hsl(var(--text-1))] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      + {t.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <SheetFooter className="border-t border-white/5 pt-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate(form)} disabled={mut.isPending || !form.label || !form.username} data-testid="drawer-submit-button">
            {mut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add credential
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
